// CONDITIONAL Power-Up cards — the ones whose effect can refuse the play for a reason the
// stage/phase gates know nothing about: what is in your hand, what is on the board, how much deck
// is left, a flag you already carry.
//
// Why the client needs to know: the dock frames a playable card in a BLUE ring meaning "you can
// play this right now". For these cards that promise is a lie — Demonic Ritual is legal on every
// street and still 400s until three 6s exist. So a card listed here never wears the blue ring;
// instead it wears a RED ring at exactly the moments both halves are true (its modifier's window
// is live AND its condition is actually met). Same treatment inside the auto-play box, where the
// red ring reads as "this is about to fire".
//
// Each predicate MIRRORS one effect bean's validate() (named in its comment) — the server stays
// the only authority, and a mismatch costs at most a wrong ring for one frame. Cards whose
// preconditions the server checks with a bespoke begin-time scan rather than validate() (the
// targeted families' "is there a valid target") are included too, since the player-visible effect
// is identical. A code absent from this table is NOT conditional and keeps the ordinary blue ring.

const RANK = (token) => (token || '').slice(0, -1);
const SUIT = (token) => (token || '').slice(-1);
const split = (cards) => (cards || '').trim().split(/\s+/).filter(Boolean);
const countRank = (tokens, rank) => tokens.filter((t) => RANK(t) === rank).length;

// ctx: { hole: [tokens], community: [tokens], deckRemaining, stageBoardStart, runItTwice,
//        players: {seat: publicSeat}, mySeat, folded, blind, halfBlind, exchangeRateTwo,
//        permanentPupCards: [codes] }
const both = (ctx) => [...ctx.hole, ...ctx.community];
const dealtIn = (p) => !!p && !p.isFolded && (p.numHoleCards || 0) > 0;
const unfoldedDealtIn = (ctx) => Object.values(ctx.players || {}).filter(dealtIn).length;
// Folded but still dealt in — Binding Contract's pickers (the mucked hand stays on the row).
const foldedDealtIn = (ctx) => Object.values(ctx.players || {})
  .filter((p) => !!p && p.isFolded && (p.numHoleCards || 0) > 0).length;
// PupPlayService.isValidTarget: another seat, unfolded, holding cards.
const hasOtherTarget = (ctx) => Object.entries(ctx.players || {})
  .some(([seat, p]) => Number(seat) !== ctx.mySeat && dealtIn(p));

const CONDITIONS = {
  // DemonicRitualEffect — three 6s among hole + community.
  DEMONIC_RITUAL: (ctx) => countRank(both(ctx), '6') >= 3,
  // JackpotEffect — three 7s among hole + community.
  JACKPOT: (ctx) => countRank(both(ctx), '7') >= 3,
  // SevenAteNineEffect — a 7, an 8 AND a 9 among hole + community.
  SEVEN_ATE_NINE: (ctx) => ['7', '8', '9'].every((r) => countRank(both(ctx), r) >= 1),
  // TrashToAcesEffect — EXACTLY two hole cards: a 2 and a 7 of different suits.
  ONE_MANS_TRASH: (ctx) => ctx.hole.length === 2
    && ctx.hole.filter((t) => RANK(t) === '2').length === 1
    && ctx.hole.filter((t) => RANK(t) === '7').length === 1
    && SUIT(ctx.hole[0]) !== SUIT(ctx.hole[1]),
  // WildCardEffect (untargeted variant) — needs a board to pick from.
  WHERE_THE_WILD_THINGS_ARE: (ctx) => ctx.community.length > 0,
  // RunItTwiceEffect — not already running twice, and five cards left for the second board.
  RUN_IT_TWICE: (ctx) => !ctx.runItTwice && ctx.deckRemaining >= 5,
  // ExtraCommunityCardEffect — one card left to add to the board.
  ADDITIONAL_SUPPORT: (ctx) => ctx.deckRemaining >= 1,
  // PowerInfusionEffect — two hole cards to draw.
  POWER_INFUSION: (ctx) => ctx.deckRemaining >= 2,
  // PremonitionEffect / AllSeeingEyeEffect — something left to look at.
  PREMONITION: (ctx) => ctx.deckRemaining >= 1,
  ALL_SEEING_EYE: (ctx) => ctx.deckRemaining >= 1,
  // ClairvoyanceEffect — a deck to peek at AND an opponent to read.
  CLAIRVOYANCE: (ctx) => ctx.deckRemaining >= 1 && hasOtherTarget(ctx),
  // SacrificeEffect / TierUpgradeEffect — a hole card to spare.
  SACRIFICE: (ctx) => ctx.hole.length >= 2,
  SACRIFICIAL_CEREMONY: (ctx) => ctx.hole.length >= 2,
  WORTHY_SACRIFICE: (ctx) => ctx.hole.length >= 2,
  // NegotiationEffect — at least two players still in the round.
  NEGOTIATION: (ctx) => unfoldedDealtIn(ctx) >= 2,
  // BindingContractEffect — at least one OTHER folded player ("you may not play this card
  // if you are the only folded player") AND at least two unfolded players to bet on.
  BINDING_CONTRACT: (ctx) => foldedDealtIn(ctx) >= 2 && unfoldedDealtIn(ctx) >= 2,
  // MindChangeEffect — a folded hand to rejoin with (the folded half is an ordinary gate).
  MIND_CHANGE: (ctx) => ctx.hole.length > 0,
  // FlyingBlindEffect / HalfBlindEffect — you cannot blind yourself twice.
  FLYING_BLIND: (ctx) => !ctx.blind,
  HALF_BLIND_BANDIT: (ctx) => !ctx.blind && !ctx.halfBlind,
  // ExchangeRateEffect — the discount does not stack.
  GOOD_ECONOMY: (ctx) => !ctx.exchangeRateTwo,
  // StreamSniperEffect — only one on the felt at a time.
  STREAM_SNIPER: (ctx) => !(ctx.permanentPupCards || []).includes('STREAM_SNIPER'),
  // PupPlayService.anyCardTarget, COMMUNITY scope + the "placed this stage" pick rule: a board
  // card dealt on THIS street. Pre-flop there is no board, so it can never fire there.
  SUBSTITUTION: (ctx) => ctx.community.length > ctx.stageBoardStart,
  // PupPlayService.isValidTarget — another unfolded player holding cards.
  SKIP: hasOtherTarget,
  SKIP_FLOP: hasOtherTarget,
  SKIP_TURN: hasOtherTarget,
  SKIP_RIVER: hasOtherTarget,
  PEEPER: hasOtherTarget,
  PEEPER_FLOP: hasOtherTarget,
  PEEPER_TURN: hasOtherTarget,
  PEEPER_RIVER: hasOtherTarget,
  // Spy Master / Button Up are targeted now (v4 text: "Target opponent/player…").
  SPY_MASTER: hasOtherTarget,
  BUTTON_UP: hasOtherTarget,
};

export const isConditionalPupCard = (code) =>
  Object.prototype.hasOwnProperty.call(CONDITIONS, code);

// Is this card's CONTENT precondition satisfied right now? Non-conditional codes answer true, so
// callers can use it unguarded. A malformed context can only cost a wrong ring, never a crash.
export const pupConditionMet = (code, ctx) => {
  const test = CONDITIONS[code];
  if (!test) return true;
  try {
    return !!test(ctx);
  } catch {
    return false;
  }
};

// Build the evaluation context once per render from the pieces GameTable already has.
export const buildPupConditionContext = ({
  game, myCards, mySeat, exchangeRateTwo,
}) => {
  const seat = game?.players?.[mySeat] || {};
  return {
    hole: Array.isArray(myCards) ? myCards.filter(Boolean) : split(myCards),
    community: split(game?.communityCards),
    deckRemaining: game?.deckRemaining ?? 0,
    stageBoardStart: game?.stageBoardStart ?? 0,
    runItTwice: !!game?.runItTwice,
    players: game?.players || {},
    mySeat,
    folded: !!seat.isFolded,
    blind: !!seat.blind,
    halfBlind: !!seat.halfBlind,
    exchangeRateTwo: !!exchangeRateTwo,
    permanentPupCards: seat.permanentPupCards || [],
  };
};
